/* RESPONSIBLE TEAM: team-standalone */
import BaseValidator from 'ember-cp-validations/validators/base';
import { inject as service } from '@ember/service';
import type Model from '@ember-data/model';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import type { HandoffToSalesforceOwnerIdByIntegration } from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';

export default class HandoffToSalesforceOwnerIdByIntegrationValid extends BaseValidator {
  @service declare intl: IntlService;

  async validate(
    _value: unknown,
    options: ValidationOptions & { messageKey: string; allowUnassigned?: boolean },
    model: Model & {
      handoffToSalesforceOwnerId: string | undefined;
      handoffToSalesforceOwnerIdByIntegration?: HandoffToSalesforceOwnerIdByIntegration;
      finStandaloneService?: FinStandaloneService;
    },
  ) {
    let finStandaloneService = model.finStandaloneService;

    if (!finStandaloneService?.salesforcePlatform) {
      return true;
    }

    await this.waitForSalesforceSetupData(finStandaloneService);

    let platform = finStandaloneService.salesforcePlatform;
    let configurations = platform.configurations;

    if (!configurations || configurations.length === 0) {
      return true;
    }

    let handoffByIntegration = model.handoffToSalesforceOwnerIdByIntegration || [];
    let errorMessage: string | null = null;

    configurations.forEach((config) => {
      if (errorMessage) {
        return;
      }

      let handoffEntry = handoffByIntegration.find((entry) => entry.integration_id === config.id);
      let handoffId = handoffEntry?.handoff_id ?? model.handoffToSalesforceOwnerId;

      if (!handoffId) {
        if (options.allowUnassigned) {
          return;
        }
        errorMessage = this.intl.t(options.messageKey);
        return;
      }

      let handoverOption = config.findHandoverOptionById(handoffId);
      if (!handoverOption) {
        errorMessage = this.intl.t(options.messageKey);
      }
    });

    return errorMessage ?? true;
  }

  static getDependentsFor() {
    return [
      'model.handoffToSalesforceOwnerId',
      'model.handoffToSalesforceOwnerIdByIntegration',
      'model.finStandaloneService.salesforcePlatform.salesforceSetupData',
      'model.finStandaloneService.salesforcePlatform.configurationData',
    ];
  }

  private async waitForSalesforceSetupData(finStandaloneService: FinStandaloneService) {
    let dataLoader = finStandaloneService.salesforcePlatform?.salesforceDataLoader;
    if (!dataLoader) {
      return;
    }

    let task = taskFor(dataLoader.loadSalesforceSetupData);
    if (!task.isRunning) {
      return;
    }

    return task.last!;
  }
}
